/**
 * HostFileBrowser — навигация по host filesystem (через /proc/1/root)
 * с просмотром и редактированием файлов через CodeMirror.
 *
 * Backend: /api/v1/devops/files/{list,read,write,mkdir,rm}.
 *
 * Защиты:
 *   - /proc /sys /dev /run — backend возвращает 403, в UI показываем ошибку.
 *   - Запись в /etc/passwd, /etc/shadow, /etc/sudoers, /etc/ssh/, /boot/,
 *     /etc/systemd/system/ — backend требует confirm=true; UI показывает диалог.
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Folder,
  FileText as FileIcon,
  Link2,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Save,
  X,
  Loader2,
  ShieldAlert,
  ArrowUp,
  Trash2,
  FolderPlus,
} from "lucide-react";
import {
  fetchHostDir,
  fetchHostFile,
  writeHostFile,
  mkdirHost,
  rmHost,
  type HostFileEntry,
} from "../../lib/api";
import { cn } from "../../lib/cn";

const CodeMirrorEditor = lazy(() => import("../files/CodeMirrorEditor"));
const detectLangKeyPromise = import("../files/CodeMirrorEditor").then((m) => m.detectLangKey);

const QUICK_DIRS = [
  "/etc/nginx/sites-available",
  "/etc/nginx/sites-enabled",
  "/etc/letsencrypt/live",
  "/etc/systemd/system",
  "/var/log",
  "/var/log/nginx",
  "/root",
  "/home",
];

export function HostFileBrowser() {
  const [path, setPath] = useState("/etc");
  const [items, setItems] = useState<HostFileEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFile, setOpenFile] = useState<{
    path: string;
    content: string;
    is_binary: boolean;
    truncated: boolean;
    dirty: boolean;
  } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    kind: "save" | "rm";
    target: string;
    action: () => void;
  } | null>(null);

  async function loadDir(p: string) {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchHostDir(p);
      setItems(r.items);
      setPath(r.path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDir("/etc");
  }, []);

  function navigate(name: string) {
    const next = path === "/" ? `/${name}` : `${path}/${name}`;
    loadDir(next);
  }

  function navigateUp() {
    if (path === "/") return;
    const parts = path.split("/").filter(Boolean);
    parts.pop();
    loadDir("/" + parts.join("/"));
  }

  async function openItem(item: HostFileEntry) {
    if (item.type === "dir") {
      navigate(item.name);
      return;
    }
    if (item.size > 5 * 1024 * 1024) {
      if (!confirm(`Файл ${item.size} байт. Точно открыть?`)) return;
    }
    const target = path === "/" ? `/${item.name}` : `${path}/${item.name}`;
    try {
      const f = await fetchHostFile(target);
      setOpenFile({
        path: f.path,
        content: f.content,
        is_binary: f.is_binary,
        truncated: f.truncated,
        dirty: false,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function saveCurrent(confirmed: boolean = false) {
    if (!openFile) return;
    setSaveBusy(true);
    try {
      const r = await writeHostFile(openFile.path, openFile.content, { confirm: confirmed });
      setOpenFile({ ...openFile, dirty: false });
      setConfirmAction(null);
      // Показать тост-toast для бэкапа
      if (r.backup_path) {
        setError(null);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ") || msg.includes("Защищ")) {
        setConfirmAction({
          kind: "save",
          target: openFile.path,
          action: () => saveCurrent(true),
        });
      } else {
        setError(msg);
      }
    } finally {
      setSaveBusy(false);
    }
  }

  async function doRm(target: string, confirmed: boolean = false) {
    try {
      await rmHost(target, { confirm: confirmed });
      setConfirmAction(null);
      loadDir(path);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ") || msg.includes("Удаление")) {
        setConfirmAction({
          kind: "rm",
          target,
          action: () => doRm(target, true),
        });
      } else {
        setError(msg);
      }
    }
  }

  async function doMkdir() {
    const name = prompt("Имя новой папки:");
    if (!name) return;
    try {
      const newPath = path === "/" ? `/${name}` : `${path}/${name}`;
      await mkdirHost(newPath);
      loadDir(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-3 h-full">
      {/* Left: tree */}
      <div className="space-y-2 min-h-0 flex flex-col">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={navigateUp}
            disabled={path === "/"}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle disabled:opacity-40"
            title="Вверх"
          >
            <ArrowUp className="size-3.5" />
          </button>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadDir(path)}
            className="flex-1 px-2 py-1 rounded border border-border/60 bg-bg-card text-small font-mono"
          />
          <button
            type="button"
            onClick={() => loadDir(path)}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Обновить"
          >
            <RefreshCw className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={doMkdir}
            className="p-1.5 rounded border border-border/60 hover:bg-bg-subtle"
            title="Новая папка"
          >
            <FolderPlus className="size-3.5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1">
          {QUICK_DIRS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => loadDir(d)}
              className="px-1.5 py-0.5 rounded text-[10px] border border-border/60 hover:bg-bg-subtle text-fg-muted hover:text-fg font-mono"
              title={d}
            >
              {d.split("/").slice(-1)[0] || "/"}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 rounded-modal border border-border/60 bg-bg-card/40 overflow-auto">
          {loading && (
            <div className="p-3 text-fg-muted text-small">
              <Loader2 className="size-4 animate-spin inline mr-2" />
              загрузка…
            </div>
          )}
          {error && !loading && (
            <div className="p-3 text-danger text-small">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="p-3 text-fg-muted text-small">(пусто)</div>
          )}
          {!loading && !error && (
            <ul className="text-small font-mono">
              {items.map((it) => (
                <li
                  key={it.name}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 hover:bg-bg-subtle/50 cursor-pointer border-b border-border/20",
                  )}
                  onClick={() => openItem(it)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (confirm(`Удалить ${it.name}?`)) {
                      const target = path === "/" ? `/${it.name}` : `${path}/${it.name}`;
                      doRm(target, false);
                    }
                  }}
                >
                  {it.type === "dir" ? (
                    <Folder className="size-3.5 text-accent-500 shrink-0" />
                  ) : it.type === "link" ? (
                    <Link2 className="size-3.5 text-violet shrink-0" />
                  ) : (
                    <FileIcon className="size-3.5 text-fg-muted shrink-0" />
                  )}
                  <span className="truncate flex-1 text-fg">{it.name}</span>
                  <span className="text-[10px] text-fg-subtle tabular-nums">
                    {it.type === "file" ? formatBytes(it.size) : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="text-[10px] text-fg-subtle font-mono">
          ПКМ — удалить · клик — открыть/перейти
        </div>
      </div>

      {/* Right: viewer/editor */}
      <div className="min-h-0 flex flex-col rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        {!openFile ? (
          <div className="flex-1 grid place-items-center text-fg-muted text-small p-6">
            Кликни по файлу слева, чтобы открыть для просмотра/редактирования.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40 bg-bg-subtle/40">
              <FileIcon className="size-3.5 text-accent-500" />
              <div className="font-mono text-small flex-1 truncate" title={openFile.path}>
                {openFile.path}
              </div>
              {openFile.dirty && (
                <span className="text-[10px] uppercase font-mono text-warning">несохранено</span>
              )}
              {openFile.truncated && (
                <span className="text-[10px] uppercase font-mono text-warning">обрезано</span>
              )}
              <button
                type="button"
                onClick={() => saveCurrent(false)}
                disabled={saveBusy || !openFile.dirty}
                className="px-2 py-1 rounded text-[11px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                {saveBusy ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />}
                Сохранить
              </button>
              <button
                type="button"
                onClick={() => {
                  if (
                    !openFile.dirty ||
                    confirm("Закрыть без сохранения?")
                  ) {
                    setOpenFile(null);
                  }
                }}
                className="p-1 rounded border border-border/60 hover:bg-bg-subtle"
                title="Закрыть"
              >
                <X className="size-3.5" />
              </button>
            </div>
            {confirmAction && confirmAction.kind === "save" && (
              <div className="m-2 rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
                <ShieldAlert className="size-4 mt-0.5 shrink-0 text-warning" />
                <div className="flex-1">
                  <div className="font-medium text-small">Защищённый путь</div>
                  <div className="text-[12px] text-fg-muted">
                    {confirmAction.target} требует подтверждения. Backend сделает автобэкап исходника.
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => confirmAction.action()}
                      className="px-3 py-1.5 rounded-md text-small font-medium text-white"
                      style={{ backgroundImage: "var(--grad-cta)" }}
                    >
                      Подтверждаю
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmAction(null)}
                      className="px-3 py-1.5 rounded-md text-small border border-border/60"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 bg-bg-subtle/30">
              {openFile.is_binary ? (
                <div className="p-4 text-fg-muted text-small">
                  Файл бинарный — отображение/редактирование отключено.
                </div>
              ) : (
                <Suspense
                  fallback={
                    <div className="p-4 text-fg-muted text-small">
                      <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка редактора…
                    </div>
                  }
                >
                  <FileEditor
                    path={openFile.path}
                    content={openFile.content}
                    onChange={(val) => setOpenFile({ ...openFile, content: val, dirty: true })}
                  />
                </Suspense>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FileEditor({
  path,
  content,
  onChange,
}: {
  path: string;
  content: string;
  onChange: (val: string) => void;
}) {
  const [lang, setLang] = useState("plain");
  useEffect(() => {
    let cancelled = false;
    detectLangKeyPromise.then((fn) => {
      if (!cancelled) setLang(fn(path));
    });
    return () => {
      cancelled = true;
    };
  }, [path]);
  return (
    <CodeMirrorEditor content={content} langKey={lang} onChange={onChange} />
  );
}

function formatBytes(b: number): string {
  if (!b || b < 0) return "0";
  const u = ["B", "K", "M", "G"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)}${u[i]}`;
}
